import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import {
  type ApiError,
  type ApiResponse,
  addActionNameToMutationResult,
  handleZodError,
  useAxiosInstance,
} from 'shared/hooks/utils';

import { AutomationSchema, type CreateAutomationVariables } from './use-create-automation';

type DeepPartial<T> = {
  [P in keyof T]?: T[P] extends object ? DeepPartial<T[P]> : T[P];
};

export type UpdateAutomationVariables = CreateAutomationVariables;

const UpdateAutomationSchema = AutomationSchema.partial();

export async function updateAutomationFn(
  axiosInstance: Promise<AxiosInstance>,
  data: DeepPartial<UpdateAutomationVariables>,
) {
  const instance = await axiosInstance;

  const safeParse = UpdateAutomationSchema.safeParse(data);
  if (!safeParse.success) handleZodError(safeParse, 'updateAutomation');

  return instance.put(`/automations/${data.id}`, safeParse.data);
}

export function useUpdateAutomation() {
  const client = useQueryClient();
  const axiosInstance = useAxiosInstance();

  const mutation = useMutation<ApiResponse, ApiError, DeepPartial<UpdateAutomationVariables>>({
    mutationFn: data => updateAutomationFn(axiosInstance, data),
    onSuccess: (_response, variables) => {
      client.invalidateQueries({ queryKey: ['automationIndex'] });
      client.invalidateQueries({ queryKey: ['automation', variables.id] });
    },
  });

  return addActionNameToMutationResult<
    ApiResponse,
    ApiError,
    DeepPartial<UpdateAutomationVariables>,
    'updateAutomation'
  >('updateAutomation', mutation);
}
